import { useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import api, { formatApiError } from "@/lib/api";
import { formatVariantLabel, sortVariants } from "@/lib/variants";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Plus, Trash2, Pencil, Search, Barcode, FileUp, Loader2, Package,
  Warehouse as WarehouseIcon, Download, Eye, X, Check as CheckIcon,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { convertLine, rateOf } from "@/lib/lineMath";
import PriceHistoryDialog, { PriceHistoryLink } from "@/components/PriceHistoryDialog";
import { SupplyField } from "@/components/order-edit/SupplyField";
import { useAuth } from "@/context/AuthContext";

// Satış İskonto Yetki Limiti — tenant ayarından okunur (varsayılan %10).
const DISCOUNT_LIMIT_FALLBACK = 10;

const CURRENCY_SYMBOL = { TRY: "₺", USD: "$", EUR: "€" };
const fmtMoney = (n, cur = "TRY") =>
  `${CURRENCY_SYMBOL[cur] || "₺"}${Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtInt = (n) => Number(n || 0).toLocaleString("tr-TR");
const IMAGE_EXT = ["jpg", "jpeg", "png", "webp", "svg"];

const emptyLine = () => ({
  product_id: "", product_name: "", variant_id: "", variant_sku: "",
  variant_attributes: {}, warehouse_id: "",
  quantity: 1, unit_price: 0, currency: "TRY",
  tax_rate: 20, discount_percent: 0, discount_amount: 0,
  notes: "", applications: [],
});

// FAZ 2 — TR ondalık parse
const parseTr = (s) => {
  let t = String(s ?? "").trim().replace(/\s/g, "");
  if (!t) return 0;
  if (t.includes(",")) t = t.replace(/\./g, "").replace(",", ".");
  const v = Number(t);
  return Number.isFinite(v) ? v : 0;
};

/**
 * Professional order-lines management module — search, variants, warehouse stock,
 * multi-currency pricing with tax/discount, per-line file uploads.
 */
export default function OrderLinesEditor({ order, onChanged, confirmed = false, changeSummary = "" }) {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [warehouses, setWarehouses] = useState([]);
  const [dialogLine, setDialogLine] = useState(null);
  const [dialogMode, setDialogMode] = useState("new"); // "new" | "edit"

  // Üretimdeki siparişte revizyon sebebi zorunlu — proaktif kontrol
  const requireReason = () => {
    if (confirmed && !changeSummary.trim()) {
      toast.error("Önce yukarıdaki Revizyon Sebebi alanını doldurun");
      return false;
    }
    return true;
  };

  const load = async () => {
    if (!order) return;
    setLoading(true);
    try {
      const [lRes, wRes] = await Promise.all([
        api.get(`/orders/${order.id}/lines`),
        api.get(`/warehouses`),
      ]);
      setLines(lRes.data || []);
      setWarehouses(wRes.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [order?.id]);

  const openNew = () => { if (!requireReason()) return; setDialogLine(emptyLine()); setDialogMode("new"); };
  const openEdit = (l) => {
    if (!requireReason()) return;
    setDialogLine({
      id: l.id,
      product_id: l.product_id, product_name: l.product_name,
      variant_id: l.variant_id || "", variant_sku: l.variant_sku || "",
      variant_attributes: l.variant_attributes || {},
      warehouse_id: l.warehouse_id || "",
      quantity: l.quantity, unit_price: l.unit_price,
      currency: l.currency || "TRY",
      tax_rate: l.tax_rate ?? 20,
      discount_percent: l.discount_percent ?? 0,
      discount_amount: l.discount_amount ?? 0,
      notes: l.notes || "",
    });
    setDialogMode("edit");
  };

  const removeLine = async (l) => {
    if (!requireReason()) return;
    if (!window.confirm(`${l.product_name} satırı silinsin mi?`)) return;
    try {
      await api.delete(`/orders/${order.id}/lines/${l.id}`, { params: { change_summary: changeSummary } });
      toast.success("Satır silindi");
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const grandTotals = useMemo(() => {
    const acc = {};
    for (const l of lines) {
      const c = l.currency || "TRY";
      acc[c] ||= { subtotal: 0, tax: 0, total: 0, qty: 0 };
      acc[c].subtotal += Number(l.line_subtotal || 0);
      acc[c].tax += Number(l.line_tax_total || 0);
      acc[c].total += Number(l.line_total || 0);
      acc[c].qty += Number(l.quantity || 0);
    }
    return acc;
  }, [lines]);

  return (
    <div className="space-y-4" data-testid="order-lines-editor">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold uppercase tracking-wider">Sipariş Satırları</div>
          <div className="text-xs text-muted-foreground">Barkod ile ürün ekleyin, varyant/depo seçin, çoklu döviz + iskonto + KDV.</div>
        </div>
        <Button size="sm" onClick={openNew} data-testid="add-order-line-btn"
          className="bg-blue-600 hover:bg-blue-500 rounded-sm text-xs uppercase tracking-wider font-semibold">
          <Plus className="w-4 h-4 mr-1" /> Yeni Satır
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></div>
      ) : lines.length === 0 ? (
        <Card className="rounded-sm bg-card border-border p-10 text-center" data-testid="lines-empty">
          <Package className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <div className="text-sm text-muted-foreground">Henüz sipariş satırı yok. "Yeni Satır" ile başlayın.</div>
        </Card>
      ) : (
        <Card className="rounded-sm bg-card border-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono">Ürün / Varyant</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono">Depo</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono text-right">Adet</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono text-right">Fiyat</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono text-right">İsk.</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono text-right">KDV</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono text-right">Ara T.</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono text-right">Toplam</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono">Dosyalar</TableHead>
                <TableHead className="w-24 text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((l) => (
                <React.Fragment key={l.id}>
                <TableRow className="border-border" data-testid={`line-row-${l.id}`}>
                  <TableCell>
                    <div className="text-sm font-medium">{l.product_name}</div>
                    <div className="text-[10px] font-semibold text-emerald-500 dark:text-emerald-400">
                      {formatVariantLabel(l.variant_attributes, l.variant_sku || "—")}
                    </div>
                  </TableCell>
                  <TableCell>
                    {l.warehouse_name ? (
                      <Badge variant="outline" className="rounded-sm text-[10px] uppercase font-mono">
                        <WarehouseIcon className="w-3 h-3 mr-1" /> {l.warehouse_name}
                      </Badge>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">{fmtInt(l.quantity)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{fmtMoney(l.unit_price, l.currency)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-blue-500">
                    {l.discount_percent > 0 ? `%${l.discount_percent}` : ""}
                    {l.discount_amount > 0 ? ` +${fmtMoney(l.discount_amount, l.currency)}` : ""}
                    {!l.discount_percent && !l.discount_amount ? "—" : ""}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">%{l.tax_rate}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{fmtMoney(l.line_subtotal, l.currency)}</TableCell>
                  <TableCell className="text-right font-mono text-sm font-bold">{fmtMoney(l.line_total, l.currency)}</TableCell>
                  <TableCell>
                    <AttachmentList orderId={order.id} line={l} onChanged={load} />
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" data-testid={`edit-line-${l.id}`}
                      onClick={() => openEdit(l)}
                      className="text-muted-foreground hover:text-foreground rounded-sm h-7 px-2">
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" data-testid={`delete-line-${l.id}`}
                      onClick={() => removeLine(l)}
                      className="text-muted-foreground hover:text-red-500 rounded-sm h-7 px-2">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
                {Array.isArray(l.applications) && l.applications.length > 0 && (
                  <TableRow className="border-border bg-muted/20" data-testid={`line-apps-${l.id}`}>
                    <TableCell colSpan={10} className="py-1.5">
                      <div className="pl-6 space-y-0.5">
                        {l.applications.map((a, i) => (
                          <div key={i} className="flex items-center justify-between text-[11px] font-mono" data-testid={`line-app-${l.id}-${i}`}>
                            <span className="text-muted-foreground">
                              + {a.service_name}
                              {a.send_to_production && (
                                <span className="ml-1 text-orange-400">→ {a.production_department_name || a.production_department_id}</span>
                              )}
                            </span>
                            <span className="text-slate-400">
                              {fmtInt(a.quantity)} {a.unit} × {fmtMoney(a.unit_price, a.currency)} · KDV %{a.tax_rate} = <b className="text-slate-200">{fmtMoney(a.total, a.currency)}</b>
                            </span>
                          </div>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>

          {/* Grand totals per currency */}
          <div className="border-t border-border p-3 bg-muted/40 flex flex-wrap gap-3 justify-end" data-testid="lines-totals">
            {Object.entries(grandTotals).map(([cur, t]) => (
              <div key={cur} className="text-right border border-border rounded-sm bg-card px-3 py-2"
                data-testid={`total-${cur}`}>
                <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Toplam ({cur})</div>
                <div className="text-xs text-muted-foreground font-mono">
                  {fmtInt(t.qty)} adet · Ara T. {fmtMoney(t.subtotal, cur)} · KDV {fmtMoney(t.tax, cur)}
                </div>
                <div className="text-lg font-bold font-mono text-blue-500">{fmtMoney(t.total, cur)}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <OrderLineDialog
        open={!!dialogLine}
        mode={dialogMode}
        line={dialogLine}
        warehouses={warehouses}
        orderId={order?.id}
        customerId={order?.customer_id}
        changeSummary={changeSummary}
        onOpenChange={(v) => !v && setDialogLine(null)}
        onSaved={async () => { setDialogLine(null); await load(); onChanged?.(); }}
      />
    </div>
  );
}

function AttachmentList({ orderId, line, onChanged }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/orders/${orderId}/lines/${line.id}/attachments/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${file.name} yüklendi`);
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const download = async (att) => {
    try {
      const resp = await api.get(`/orders/${orderId}/lines/${line.id}/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const preview = (att) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/lines/${line.id}/attachments/${att.id}/download`, "_blank");
  };

  const remove = async (att) => {
    if (!window.confirm(`${att.original_filename} silinsin mi?`)) return;
    try {
      await api.delete(`/orders/${orderId}/lines/${line.id}/attachments/${att.id}`);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const atts = line.attachments || [];
  return (
    <div className="flex flex-wrap gap-1 items-center max-w-[220px]" data-testid={`attachments-${line.id}`}>
      {atts.map((a) => {
        const ext = (a.original_filename || "").split(".").pop().toLowerCase();
        const isImage = IMAGE_EXT.includes(ext);
        return (
          <div key={a.id} className="group relative">
            <button title={a.original_filename}
              onClick={() => (isImage ? preview(a) : download(a))}
              data-testid={`att-chip-${a.id}`}
              className="text-[10px] px-1.5 py-0.5 rounded-sm border border-border bg-muted hover:bg-muted/60 uppercase font-mono flex items-center gap-1 max-w-[80px]">
              <span className="truncate">{ext}</span>
            </button>
            <div className="absolute right-0 top-full mt-0.5 hidden group-hover:flex bg-card border border-border rounded-sm shadow z-10">
              {isImage && (
                <button onClick={() => preview(a)} title="Önizle" className="p-1 hover:bg-muted">
                  <Eye className="w-3 h-3" />
                </button>
              )}
              <button onClick={() => download(a)} title="İndir" className="p-1 hover:bg-muted">
                <Download className="w-3 h-3" />
              </button>
              <button onClick={() => remove(a)} title="Sil" className="p-1 hover:bg-red-500/20 text-red-500">
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        );
      })}
      <input ref={inputRef} type="file" hidden accept=".ai,.cdr,.pdf,.png,.jpg,.jpeg,.svg,.eps,.psd,.webp"
        data-testid={`upload-input-${line.id}`}
        onChange={upload} />
      <button onClick={() => inputRef.current?.click()} title="Dosya Ekle"
        data-testid={`upload-attachment-${line.id}`}
        className="text-[10px] px-1.5 py-0.5 rounded-sm border border-dashed border-border hover:border-blue-500 text-muted-foreground hover:text-blue-500">
        {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileUp className="w-3 h-3" />}
      </button>
    </div>
  );
}

// ---- Line editor dialog ----------------------------------------------------
const appsSig = (apps) => JSON.stringify((apps || []).map((a) => [a.service_id, Number(a.quantity || 0), Number(a.unit_price || 0), a.currency, Number(a.tax_rate || 0)]));

// Detaylı satır düzenleme. `onApply` verilirse taslak modunda çalışır (API'ye yazmaz; çalışma alanı toplu kaydeder).
export function OrderLineDialog({ open, mode, line, warehouses, orderId, customerId, changeSummary = "", onOpenChange, onSaved,
  onApply, fxRates: fxProp, readOnly = false, persisted = false, draftKey = "", onAttachmentsChanged, onDelete,
  supplyEditable = false, supplyIsOrder = false }) {
  const { can } = useAuth();
  const canOverDiscount = can?.("orders", "discount_over_limit") || false;
  const [form, setForm] = useState(emptyLine());
  const [variants, setVariants] = useState([]);
  const [stock, setStock] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [fxRates, setFxRates] = useState(null);
  const [phOpen, setPhOpen] = useState(false); // Önceki Fiyatlar dialogu
  const [discLimit, setDiscLimit] = useState(DISCOUNT_LIMIT_FALLBACK);
  const searchTimer = useRef(null);
  const [pendingCur, setPendingCur] = useState(null);
  useEffect(() => { api.get("/settings/discount-limit").then(({ data }) => setDiscLimit(Number(data?.approval_limit_percent ?? DISCOUNT_LIMIT_FALLBACK))).catch(() => {}); }, []);

  // Geçmiş fiyatı aktar — fiyat ve para birimi aynen gelir (kur çevrimi yok)
  const pickPrice = (price, cur) => {
    setPendingCur(null);
    setForm((f) => ({ ...f, unit_price: price, currency: cur }));
  };

  // KÖK NEDEN DÜZELTMESİ: eskiden para birimi değişimi bir useEffect ile "otomatik" çevriliyordu; dialog yeni satırla
  // açılırken önceki satırın para birimi referans alındığı için ilk açılışta kayıtlı fiyat yeniden dönüştürülüyordu.
  // Artık çeviri yalnız kullanıcının bilinçli para birimi seçimi + onayı ile yapılır; açılış hiçbir şeyi çevirmez.
  useEffect(() => {
    if (!open || fxProp) return;
    api.get(`/finance/exchange-rates`).then(({ data }) => setFxRates(data)).catch(() => {});
  }, [open, fxProp]);
  const fx = fxProp || fxRates;
  const hasMoney = (f) => Number(f.unit_price) > 0 || Number(f.discount_amount) > 0 || (f.applications || []).length > 0;
  const onCurrencyPick = (v) => {
    if (v === form.currency) { setPendingCur(null); return; }
    if (!hasMoney(form)) {
      setForm((f) => ({ ...f, currency: v, applications: (f.applications || []).map((a) => ({ ...a, currency: v })) }));
      return;
    }
    setPendingCur(v);
  };
  const pendingPreview = pendingCur && rateOf(fx, pendingCur) && rateOf(fx, form.currency) ? convertLine(form, pendingCur, fx) : null;

  useEffect(() => {
    if (open && line) {
      setForm({ ...emptyLine(), ...line, currency: line.currency || "TRY", applications: Array.isArray(line.applications) ? line.applications : [] });
      setPendingCur(null);
      setSearchQ(line.product_name || "");
      setShowResults(false);
      if (line.product_id) loadVariants(line.product_id); else setVariants([]);
    }
    // eslint-disable-next-line
  }, [open, draftKey, line?.id, line?.product_id]);

  // FAZ 2/3 — Aktif hizmetleri ve üretim departmanı etiketlerini yükle
  const [services, setServices] = useState([]);
  const [deptLabels, setDeptLabels] = useState({});
  useEffect(() => {
    if (!open) return;
    api.get("/services", { params: { include_inactive: false } })
      .then(({ data }) => setServices(data || []))
      .catch(() => setServices([]));
    api.get("/departments/meta")
      .then(({ data }) => setDeptLabels(data?.labels || {}))
      .catch(() => setDeptLabels({}));
  }, [open]);

  const loadVariants = async (pid) => {
    if (!pid) { setVariants([]); return; }
    try {
      const { data } = await api.get(`/products/${pid}/variants`);
      setVariants(data || []);
    } catch (e) { setVariants([]); }
  };

  // Debounced product/barcode search
  useEffect(() => {
    if (!open) return;
    if (searchQ.length < 2) { setSearchResults([]); return; }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/products/search?q=${encodeURIComponent(searchQ)}`);
        setSearchResults(data || []);
        setShowResults(true);
      } catch (e) { /* ignore */ }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(searchTimer.current);
  }, [searchQ, open]);

  const pickResult = (r) => {
    setForm((f) => ({
      ...f,
      product_id: r.product.id, product_name: r.product.name,
      variant_id: r.variant?.id || "", variant_sku: r.variant?.sku || "",
      variant_attributes: r.variant?.attributes || {},
      unit_price: f.unit_price || r.product.base_price || 0,
    }));
    setSearchQ(r.product.name);
    setShowResults(false);
    loadVariants(r.product.id);
  };

  const onVariantChange = (vid) => {
    const v = variants.find((x) => x.id === vid);
    setForm((f) => ({
      ...f,
      variant_id: vid, variant_sku: v?.sku || "",
      variant_attributes: v?.attributes || {},
    }));
  };

  // Fetch stock when product+variant+warehouse ready
  useEffect(() => {
    if (!form.product_id || !form.warehouse_id) { setStock(null); return; }
    (async () => {
      try {
        const params = new URLSearchParams({
          product_id: form.product_id,
          warehouse_id: form.warehouse_id,
          variant_id: form.variant_id || "",
        });
        const { data } = await api.get(`/inventory/stock?${params.toString()}`);
        setStock(data);
      } catch (e) { setStock(null); }
    })();
  }, [form.product_id, form.variant_id, form.warehouse_id]);

  // Compute totals live (FAZ 2 — uygulamalar dahil)
  const totals = useMemo(() => {
    const qty = Number(form.quantity || 0);
    const price = Number(form.unit_price || 0);
    const gross = qty * price;
    const pctD = gross * (Number(form.discount_percent || 0) / 100);
    const flatD = Number(form.discount_amount || 0);
    const prodSub = Math.max(0, gross - pctD - flatD);
    const prodTax = prodSub * (Number(form.tax_rate || 0) / 100);
    let appsSub = 0, appsTax = 0;
    for (const a of (form.applications || [])) {
      const q = Number(a.quantity || 0);
      const p = Number(a.unit_price || 0);
      const r = Number(a.tax_rate || 0);
      const s = q * p;
      appsSub += s;
      appsTax += s * r / 100;
    }
    const sub = prodSub + appsSub;
    const tax = prodTax + appsTax;
    return { gross, pctD, flatD, sub, tax, total: sub + tax, appsSub, appsTax };
  }, [form.quantity, form.unit_price, form.discount_percent, form.discount_amount, form.tax_rate, form.applications]);

  const submit = async () => {
    if (!form.product_id) return toast.error("Ürün seçin");
    if (!form.quantity || Number(form.quantity) <= 0) return toast.error("Adet 0'dan büyük olmalı");
    if (pendingCur) return toast.error("Para birimi değişimini onaylayın veya vazgeçin");
    if (onApply) {
      onApply({
        ...form,
        quantity: Math.round(Number(form.quantity)), unit_price: Number(form.unit_price || 0), tax_rate: Number(form.tax_rate || 0),
        discount_percent: Number(form.discount_percent || 0), discount_amount: Number(form.discount_amount || 0),
        currency: form.currency || "TRY",
        warehouse_name: (warehouses.find((w) => w.id === form.warehouse_id) || {}).name || "",
        _appsChanged: appsSig(form.applications) !== appsSig(line?.applications) || !!line?._appsChanged,
      });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        product_id: form.product_id,
        product_name: form.product_name,
        variant_id: form.variant_id || "",
        variant_sku: form.variant_sku || "",
        variant_attributes: form.variant_attributes || {},
        warehouse_id: form.warehouse_id || "",
        quantity: Number(form.quantity),
        unit_price: Number(form.unit_price || 0),
        currency: form.currency || "TRY",
        tax_rate: Number(form.tax_rate || 0),
        discount_percent: Number(form.discount_percent || 0),
        discount_amount: Number(form.discount_amount || 0),
        notes: form.notes || "",
        applications: (form.applications || []).map((a) => ({
          service_id: a.service_id || "",
          service_code: a.service_code || "",
          service_name: a.service_name || "",
          quantity: Number(a.quantity || 0),
          unit: a.unit || "adet",
          unit_price: Number(a.unit_price || 0),
          currency: a.currency || form.currency || "TRY",
          tax_rate: Number(a.tax_rate || 0),
          send_to_production: Boolean(a.send_to_production),
          production_department_id: a.production_department_id || "",
          production_department_name: a.production_department_name || "",
        })),
        change_summary: changeSummary || "",
      };
      if (mode === "edit" && line.id) {
        await api.patch(`/orders/${orderId}/lines/${line.id}`, payload);
        toast.success("Satır güncellendi");
      } else {
        await api.post(`/orders/${orderId}/lines`, payload);
        toast.success("Satır eklendi");
      }
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const stockAvailable = stock ? Number(stock.available ?? stock.quantity ?? 0) : null;
  const stockShort = stockAvailable !== null && stockAvailable < Number(form.quantity || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="line-dialog"
        onEscapeKeyDown={(e) => { if (pendingCur) { e.preventDefault(); setPendingCur(null); } }}
        className="bg-card border-border w-[96vw] max-w-[1080px] max-h-[92vh] overflow-y-auto p-0 gap-0
          max-sm:w-screen max-sm:max-w-none max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:rounded-none max-sm:border-0">
        <DialogHeader className="px-6 py-4 border-b border-border sticky top-0 z-20 bg-card">
          <DialogTitle className="flex items-center gap-2 text-base">
            {mode === "edit" ? <Pencil className="w-4 h-4 text-blue-500" /> : <Plus className="w-4 h-4 text-blue-500" />}
            {mode === "edit" ? "Satırı Detaylı Düzenle" : "Yeni Satır"}
            {onApply && <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground hidden sm:inline">değişiklikler tabloya aktarılır, kayıt toplu yapılır</span>}
          </DialogTitle>
        </DialogHeader>
        <fieldset disabled={readOnly} className="px-6 py-5 space-y-5 min-w-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <div className="space-y-4 min-w-0" data-testid="line-dialog-left">
          {/* Product search (barcode-capable) */}
          <div className="space-y-1.5 relative">
            <Label className="text-xs uppercase font-mono text-muted-foreground flex items-center gap-1.5">
              <Barcode className="w-3.5 h-3.5" /> Ürün / Barkod / SKU Ara
            </Label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-3 text-muted-foreground" />
              <Input value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                onFocus={() => setShowResults(searchResults.length > 0)}
                data-testid="line-search-input"
                placeholder="Ürün adı, SKU veya barkod okutun"
                className="rounded-sm pl-8 h-10 font-mono" />
              {searching && <Loader2 className="w-3.5 h-3.5 animate-spin absolute right-3 top-3 text-muted-foreground" />}
            </div>
            {showResults && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 z-30 bg-popover border border-border rounded-sm max-h-60 overflow-y-auto shadow-lg"
                data-testid="line-search-results">
                {searchResults.map((r, i) => (
                  <button key={`${r.product.id}-${r.variant?.id || i}`}
                    data-testid={`search-result-${i}`}
                    onClick={() => pickResult(r)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center justify-between gap-2 border-b border-border last:border-b-0">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{r.product.name}</div>
                      {r.variant && (
                        <div className="text-[10px] text-muted-foreground font-mono">
                          {r.variant.sku} · {Object.entries(r.variant.attributes || {}).map(([k, v]) => `${k}:${v}`).join(" · ")}
                        </div>
                      )}
                    </div>
                    <Badge variant="outline" className="text-[10px] uppercase font-mono">
                      {r.match_reason === "barcode" ? "barkod" : r.match_reason === "sku" ? "sku" : "isim"}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Variant + warehouse */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-mono text-muted-foreground">Varyant</Label>
              <Select value={form.variant_id || "none"} onValueChange={(v) => onVariantChange(v === "none" ? "" : v)}
                disabled={!form.product_id}>
                <SelectTrigger className="rounded-sm h-10" data-testid="line-variant-select">
                  <SelectValue placeholder={form.product_id ? "Varyant seç" : "Önce ürün seçin"} />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="none">Varyantsız</SelectItem>
                  {sortVariants(variants).map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {formatVariantLabel(v.attributes, v.sku || "—")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-mono text-muted-foreground flex items-center gap-1.5">
                <WarehouseIcon className="w-3.5 h-3.5" /> Depo
              </Label>
              <Select value={form.warehouse_id || "none"} onValueChange={(v) => setForm({ ...form, warehouse_id: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-sm h-10" data-testid="line-warehouse-select">
                  <SelectValue placeholder="Depo seç" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Depo yok</SelectItem>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {onApply && <SupplyField form={form} setForm={setForm} editable={supplyEditable && !readOnly} isOrder={supplyIsOrder}
            available={stockAvailable} />}

          {/* Stock indicator */}
          {stock && (
            <div className={`rounded-sm border px-3 py-2 text-xs font-mono flex items-center justify-between ${
              stockShort ? "border-red-500/40 bg-red-500/10 text-red-500"
                : "border-emerald-500/40 bg-emerald-500/10 text-emerald-500"
            }`} data-testid="stock-indicator">
              <span className="flex items-center gap-1.5">
                {stockShort ? <AlertTriangle className="w-3.5 h-3.5" /> : <CheckIcon className="w-3.5 h-3.5" />}
                {form.warehouse_id
                  ? `Bu depoda mevcut: ${stockAvailable} adet`
                  : "Depo seçilmedi"}
              </span>
              <span className="uppercase tracking-wider text-[10px]">
                {stockShort ? "Yetersiz Stok" : "Yeterli"}
              </span>
            </div>
          )}

              <FieldNumber label="Adet" testId="line-qty" value={form.quantity}
                onChange={(v) => setForm({ ...form, quantity: v })} />
            </div>
            <div className="space-y-4 min-w-0" data-testid="line-dialog-right">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase font-mono text-muted-foreground">Para Birimi</Label>
                  <Select value={form.currency} onValueChange={onCurrencyPick}>
                    <SelectTrigger className="rounded-sm h-10" data-testid="line-currency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TRY">₺ TRY</SelectItem>
                      <SelectItem value="USD">$ USD</SelectItem>
                      <SelectItem value="EUR">€ EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <FieldNumber label={`Birim Fiyat (${CURRENCY_SYMBOL[form.currency]})`} testId="line-unit-price" step="0.01"
                  value={form.unit_price} onChange={(v) => setForm({ ...form, unit_price: v })} />
              </div>
              {pendingCur && (
                <div className="rounded-sm border border-blue-500/40 bg-blue-500/10 px-3 py-2 text-xs space-y-2" data-testid="line-currency-preview">
                  {pendingPreview ? (
                    <div className="font-mono">
                      {CURRENCY_SYMBOL[form.currency]}{Number(form.unit_price || 0).toFixed(2)} {form.currency} → <b>{CURRENCY_SYMBOL[pendingCur]}{pendingPreview.unit_price.toFixed(2)} {pendingCur}</b>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {[form.currency, pendingCur].filter((c) => c !== "TRY").map((c) => `1 ${c} = ₺${rateOf(fx, c).toFixed(4)}${fx?.[c]?.date ? ` (${fx[c].date}${fx[c].source === "order" ? " · sipariş kuru" : ""})` : ""}`).join(" · ")}
                      </div>
                    </div>
                  ) : <div className="text-rose-400">Kur bilgisi alınamadı — dönüşüm yapılamaz.</div>}
                  <div className="flex gap-2">
                    <Button type="button" size="sm" disabled={!pendingPreview} data-testid="line-currency-convert"
                      onClick={() => { setForm((f) => ({ ...f, ...convertLine(f, pendingCur, fx) })); setPendingCur(null); }}
                      className="h-7 rounded-sm bg-blue-600 hover:bg-blue-500 text-xs">Çevir</Button>
                    <Button type="button" size="sm" variant="outline" data-testid="line-currency-cancel"
                      onClick={() => setPendingCur(null)} className="h-7 rounded-sm text-xs">Vazgeç</Button>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <FieldNumber label="İskonto (%)" testId="line-disc-pct" step="0.5"
                  value={form.discount_percent} onChange={(v) => setForm({ ...form, discount_percent: v })} />
                <FieldNumber label={`İsk. Tutar (${CURRENCY_SYMBOL[form.currency]})`} testId="line-disc-amount" step="0.01"
                  value={form.discount_amount} onChange={(v) => setForm({ ...form, discount_amount: v })} />
                <FieldNumber label="KDV (%)" testId="line-tax-rate" step="0.5"
                  value={form.tax_rate} onChange={(v) => setForm({ ...form, tax_rate: v })} />
              </div>
              <PriceHistoryLink productId={form.product_id} onClick={() => setPhOpen(true)} testId="line-price-history" />
          {(() => {
            const gross = Number(form.quantity || 0) * Number(form.unit_price || 0);
            const eff = Number(form.discount_percent || 0) + (gross > 0 ? (Number(form.discount_amount || 0) / gross) * 100 : 0);
            if (eff <= discLimit + 1e-9) return null;
            return (
              <div data-testid="line-disc-authority-warn"
                   className={`rounded-sm border px-3 py-2 text-xs flex items-center gap-2 ${canOverDiscount ? "border-amber-500/40 bg-amber-500/10 text-amber-500" : "border-rose-500/40 bg-rose-500/10 text-rose-500"}`}>
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                {canOverDiscount
                  ? `Dikkat: Satır efektif iskonto %${eff.toFixed(1)} (limit %${discLimit}). Yönetici yetkinizle kaydedilebilir.`
                  : `Satır efektif iskonto %${eff.toFixed(1)} — %${discLimit} üstü iskonto yönetici onayı ister; teklif "onay bekliyor" olarak kaydedilir.`}
              </div>
            );
          })()}

          {/* Live totals */}
          <Card className="rounded-sm bg-muted/40 border-border p-3" data-testid="line-totals-preview">
            <div className="grid grid-cols-4 gap-2 text-xs">
              <TotalCell label="Brüt" value={fmtMoney(totals.gross, form.currency)} />
              <TotalCell label="İskonto" value={`-${fmtMoney(totals.pctD + totals.flatD, form.currency)}`} color="orange" />
              <TotalCell label="Ara Toplam" value={fmtMoney(totals.sub, form.currency)} testId="preview-subtotal" />
              <TotalCell label="KDV" value={fmtMoney(totals.tax, form.currency)} />
            </div>
            <div className="mt-2 pt-2 border-t border-border flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest font-mono text-muted-foreground">Genel Toplam</div>
                {rateOf(fx, form.currency) && form.currency !== "TRY" ? (
                  <div className="text-[10px] font-mono text-muted-foreground mt-0.5" data-testid="try-equivalent">
                    ≈ ₺{(totals.total * rateOf(fx, form.currency)).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    <span className="ml-1 text-[9px] opacity-70">
                      (1 {form.currency} = ₺{rateOf(fx, form.currency).toFixed(4)} · {fx?.[form.currency]?.date || fx?.date || ""})
                    </span>
                  </div>
                ) : null}
              </div>
              <div className="text-xl font-bold font-mono text-blue-500" data-testid="preview-total">
                {fmtMoney(totals.total, form.currency)}
              </div>
            </div>
          </Card>

            </div>
          </div>
          {/* FAZ 2 — Uygulamalar (Nakış/Baskı/Transfer vb.) */}
          <Card className="rounded-sm border-border p-3 space-y-2" data-testid="line-applications">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider font-mono">Uygulamalar</div>
                <div className="text-[10px] text-muted-foreground">Ürüne ek nakış / baskı / transfer. Ürün fiyatını etkilemez, satır toplamına eklenir.</div>
              </div>
              <Select value=""
                onValueChange={(sid) => {
                  const svc = services.find((s) => s.id === sid);
                  if (!svc) return;
                  setForm((f) => ({
                    ...f,
                    applications: [...(f.applications || []), {
                      service_id: svc.id, service_code: svc.code, service_name: svc.name,
                      quantity: Number(f.quantity || 1), unit: svc.unit || "adet",
                      unit_price: Number(svc.sale_price || 0),
                      currency: svc.currency || f.currency || "TRY",
                      tax_rate: Number(svc.tax_rate || 0),
                      send_to_production: Boolean(svc.send_to_production),
                      production_department_id: svc.production_department_id || "",
                      production_department_name: deptLabels[svc.production_department_id] || "",
                    }],
                  }));
                }}>
                <SelectTrigger className="w-56 h-8 rounded-sm text-xs" data-testid="app-add-select">
                  <SelectValue placeholder="+ Uygulama Ekle" />
                </SelectTrigger>
                <SelectContent>
                  {services.length === 0 ? (
                    <SelectItem value="none" disabled>Aktif uygulama tanımı yok</SelectItem>
                  ) : services.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`app-choose-${s.code}`}>
                      {s.name} — {CURRENCY_SYMBOL[s.currency] || "₺"}{Number(s.sale_price || 0).toFixed(2)}/{s.unit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(form.applications || []).length === 0 ? (
              <div className="text-[11px] text-muted-foreground text-center py-2 border border-dashed border-border rounded-sm">
                Bu satıra henüz uygulama eklenmedi.
              </div>
            ) : (
              <div className="space-y-1.5">
                {(form.applications || []).map((a, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center border border-border rounded-sm p-2" data-testid={`app-row-${idx}`}>
                    <div className="col-span-4">
                      <div className="text-xs font-medium">{a.service_name}</div>
                      <div className="text-[10px] font-mono text-muted-foreground">{a.service_code}</div>
                      {a.send_to_production && (
                        <div className="text-[9px] font-mono text-orange-400 mt-0.5" data-testid={`app-production-${idx}`}>
                          Üretim: {a.production_department_name || deptLabels[a.production_department_id] || a.production_department_id}
                        </div>
                      )}
                    </div>
                    <div className="col-span-2">
                      <Input type="text" inputMode="decimal" className="h-7 text-right font-mono text-xs"
                        value={a.quantity} data-testid={`app-qty-${idx}`}
                        onChange={(e) => setForm((f) => {
                          const arr = [...(f.applications || [])];
                          arr[idx] = { ...arr[idx], quantity: parseTr(e.target.value) };
                          return { ...f, applications: arr };
                        })} />
                      <div className="text-[9px] text-muted-foreground text-right mt-0.5">{a.unit}</div>
                    </div>
                    <div className="col-span-2">
                      <Input type="text" inputMode="decimal" className="h-7 text-right font-mono text-xs"
                        value={a.unit_price} data-testid={`app-price-${idx}`}
                        onChange={(e) => setForm((f) => {
                          const arr = [...(f.applications || [])];
                          arr[idx] = { ...arr[idx], unit_price: parseTr(e.target.value) };
                          return { ...f, applications: arr };
                        })} />
                      <div className="text-[9px] text-muted-foreground text-right mt-0.5">{CURRENCY_SYMBOL[a.currency] || "₺"}/{a.unit}</div>
                    </div>
                    <div className="col-span-1 text-center">
                      <Input type="text" inputMode="decimal" className="h-7 text-right font-mono text-xs"
                        value={a.tax_rate} data-testid={`app-tax-${idx}`}
                        onChange={(e) => setForm((f) => {
                          const arr = [...(f.applications || [])];
                          arr[idx] = { ...arr[idx], tax_rate: parseTr(e.target.value) };
                          return { ...f, applications: arr };
                        })} />
                      <div className="text-[9px] text-muted-foreground text-right mt-0.5">KDV %</div>
                    </div>
                    <div className="col-span-2 text-right font-mono text-sm font-semibold" data-testid={`app-total-${idx}`}>
                      {fmtMoney(Number(a.quantity || 0) * Number(a.unit_price || 0) * (1 + Number(a.tax_rate || 0) / 100), a.currency)}
                    </div>
                    <div className="col-span-1 text-right">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-400"
                        data-testid={`app-remove-${idx}`}
                        onClick={() => setForm((f) => ({ ...f, applications: (f.applications || []).filter((_, i) => i !== idx) }))}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="text-right text-[11px] font-mono text-muted-foreground pt-1">
                  Uygulamalar Ara T. {fmtMoney(totals.appsSub, form.currency)} · KDV {fmtMoney(totals.appsTax, form.currency)}
                </div>
              </div>
            )}
          </Card>

          <div className={`grid grid-cols-1 ${persisted && line?.id ? "md:grid-cols-2" : ""} gap-x-8 gap-y-4`}>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-mono text-muted-foreground">Satır Notları</Label>
              <Textarea rows={2} value={form.notes} data-testid="line-notes" onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="rounded-sm resize-none" />
            </div>
            {persisted && line?.id && (
              <div className="space-y-1.5" data-testid="line-attachments">
                <Label className="text-xs uppercase font-mono text-muted-foreground">Satır Dosyaları</Label>
                <AttachmentList orderId={orderId} line={line} onChanged={onAttachmentsChanged} />
              </div>
            )}
          </div>
        </fieldset>
        <DialogFooter className="px-6 py-3 border-t border-border sticky bottom-0 bg-card z-20 gap-2">
          {onApply && mode === "edit" && !readOnly && onDelete && (
            <Button variant="ghost" onClick={() => onDelete(line.id)} data-testid="line-delete-draft" className="mr-auto text-rose-400 rounded-sm">
              <Trash2 className="w-4 h-4 mr-1" /> Satırı Sil
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="line-cancel-btn">Kapat</Button>
          {!readOnly && (
            <Button onClick={submit} disabled={saving} data-testid="line-save-btn" className="bg-blue-600 hover:bg-blue-500 rounded-sm">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : onApply ? (mode === "edit" ? "Uygula" : "Tabloya Ekle") : (mode === "edit" ? "Güncelle" : "Ekle")}
            </Button>
          )}
        </DialogFooter>
        {/* Önceki Fiyatlar — yalnız birim fiyat + para birimi aktarılır */}
        <PriceHistoryDialog
          open={phOpen}
          onOpenChange={setPhOpen}
          productId={form.product_id}
          productName={form.product_name}
          customerId={customerId}
          onPick={pickPrice}
        />
      </DialogContent>
    </Dialog>
  );
}

function FieldNumber({ label, value, onChange, step = "1", testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase font-mono text-muted-foreground">{label}</Label>
      <Input type="number" step={step} value={value} data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-sm font-mono h-10" />
    </div>
  );
}

function TotalCell({ label, value, color, testId }) {
  const cls = color === "orange" ? "text-blue-500" : "text-foreground";
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">{label}</div>
      <div className={`font-mono font-semibold ${cls}`}>{value}</div>
    </div>
  );
}
